import React from "react";
import {Button, CheckBox, Row, Select, Spinner, Text} from "react-vcomponents";
import {BlobToString, InfoButton, RunInAction_Set} from "web-vcore";
import {store} from "../../../../Store/index.js";
import {GetAppServerURL, GetWebServerURL} from "../../../../Utils/LibIntegrations/Apollo.js";
import {GetUserInfoJWTString} from "../../../../Utils/AutoRuns/UserInfoCheck.js";
import {ErrToStr} from "../../../../Utils/General/Errors.js";
import {LHQScene_latest} from "../../../Tools/VR/LHQ/Scene.js";
import * as THREE from "three";
import {observer_mgl} from "mobx-graphlink";
import {ImageEncodeFormat, ImageEncodeFormat_values} from "../../../../Store/main/testing.js";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";

export async function LoadLightmapFromServer() {
	const lightmapState = store.main.testing.vrLightmap;

	// the web-server is configured to cache non-html files for 1 day; so for this exception (where we want to always get the latest image), we add a timestamp query-param to break the cache each time
	const response = await fetch(GetWebServerURL(`${vrLightmap_assetPath}?t=${Date.now()}`));
	const imageBlob = await response.blob(); // This preserves the original MIME type
	//console.log("Image Blob URL:", URL.createObjectURL(imageBlob));
	/*const imageStr = await BlobToString(imageBlob, "readAsDataURL");
	if (!imageStr.startsWith(imageBase64_prefix)) {
		throw new Error(`Image string does not start with expected prefix "${imageBase64_prefix}". @imageStr:${imageStr}`);
	}*/

	//console.log("Got lightmap image string from server:", imageStr);
	const texture = await BlobOrBase64ToFloatBasedDataTexture(imageBlob);
	lightmapState.StartBake_FromImage(texture);
}

export const LightmapSettingsUI = observer_mgl(()=>{
	const lightmapState = store.main.testing.vrLightmap;
	return (
		<>
			<Row mt={5} style={{fontWeight: "bold"}}>VR lightmap</Row>
			<Row mt={5}>
				<CheckBox text="Debug lightmap" value={lightmapState.debugLightmap} onChange={val=>RunInAction_Set(()=>lightmapState.debugLightmap = val)}/>
				<Text ml={5}>(should disable in actual vr headset)</Text>
				<InfoButton ml={5} text={`
					In real vr-headset, <Hud> comp's "createPortal" fails to keep the hud contents/children out of the main scene/xr-camera's view.
					So it's recommended to keep this setting disabled there.
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<Button text="Load from server" onClick={()=>LoadLightmapFromServer()}/>
				<CheckBox ml={5} text="Auto-load (on vr-page load)" value={lightmapState.autoLoadLightmapFromServer} onChange={val=>RunInAction_Set(()=>lightmapState.autoLoadLightmapFromServer = val)}/>
				<Text ml={5}>Model load wait time:</Text>
				<Spinner ml={5} value={lightmapState.modelLoadWaitTime} onChange={val=>RunInAction_Set(()=>lightmapState.modelLoadWaitTime = val)} min={0} step={1}/>
			</Row>
			<Row mt={5}>
				<Button text="Bake" onClick={()=>{
					lightmapState.StartBake_Fresh(lightmapState.renderSteps);
				}}/>
				<Text ml={5}>Resolution</Text>
				<Spinner ml={5} value={lightmapState.resolution} onChange={val=>RunInAction_Set(()=>lightmapState.resolution = val)}/>
				<Text ml={5}>Render steps:</Text>
				<Spinner ml={5} value={lightmapState.renderSteps} onChange={val=>RunInAction_Set(()=>lightmapState.renderSteps = val)}/>
				<Text ml={5} title="Max render-steps per frame">Max RS/frame:</Text>
				<Spinner ml={5} value={lightmapState.maxRenderStepsPerFrame} onChange={val=>RunInAction_Set(()=>lightmapState.maxRenderStepsPerFrame = val)}/>
			</Row>
			<Row mt={5}>
				<Button text="Upload to server" onClick={async()=>{
					const renderer = LHQScene_latest?.renderer;
					const lightmapTexture_renderTarget = LHQScene_latest?.lightmap?.progressiveLightMap2;
					if (!renderer || !lightmapTexture_renderTarget) {
						console.error("Renderer or lightmap texture render target not available.");
						return;
					}

					//const lightmapTextureAsBase64 = lightmapTexture?.image?.toDataURL("image/png");
					const lightmapTexture_base64 = await RenderTargetToBase64(renderer, lightmapTexture_renderTarget, `image/${lightmapState.format}`, lightmapState.quality);
					//const lightmapTexture_base64 = g.lastUpdate_textureDataUri;
					console.log("Prepping for sending lightmap to server. @renderTarget:", lightmapTexture_renderTarget, "@base64:", lightmapTexture_base64);

					// note: if graphql error was returned, a JS error gets thrown (so no actual data-checks needed)
					const data = await GQLRequest_Simple(`
						mutation UploadAsset($input: UploadAssetInput!) {
							uploadAsset(input: $input) { __typename }
						}
					`, {
						input: {
							path: vrLightmap_assetPath,
							dataBase64: lightmapTexture_base64.replace(imageBase64_prefix(lightmapState.format), ""), // remove the prefix
						},
					});
					//return data.uploadAsset.success;
					console.log("Data from uploadAsset:", JSON.stringify(data));
					return data.uploadAsset.__typename == "GenericResponse"; // defensive
				}}/>
				<Text ml={5}>Format:</Text>
				<Select ml={5} options={ImageEncodeFormat_values} value={lightmapState.format} onChange={val=>RunInAction_Set(()=>lightmapState.format = val as ImageEncodeFormat)}/>
				{lightmapState.format != "png" && <>
					<Text ml={5}>Quality:</Text>
					<SpinnerForPercent ml={5} value={lightmapState.quality} onChange={val=>RunInAction_Set(()=>lightmapState.quality = val)}/>
				</>}
			</Row>
		</>
	);
});

export const vrLightmap_assetPath = "Generated/VRLightmap.image"; // end with ".image", since the format is variable (unfortunately, this means opening url in browser downloads rather than views, but oh well)
export const imageBase64_prefix = ext=>`data:image/${ext};base64,`;

export async function GQLRequest_Simple(query: string, variables = {}) {
	const jwtToken = GetUserInfoJWTString();
	if (!jwtToken) {
		throw new Error("User is not authenticated. Cannot make GraphQL request.");
	}
	return await GQLRequest(GetWebServerURL("/api/graphql"), jwtToken, query, variables);
}

/**
 * Makes a GraphQL request to the server
 * @param {string} query - GraphQL query or mutation string
 * @param {Object} variables - Variables for the query
 * @returns {Promise<any>} - Response data
 */
export async function GQLRequest(gqlEndpoint: string, jwtToken: string, query: string, variables = {}) {
	const response = await fetch(gqlEndpoint, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			authorization: `Bearer ${jwtToken}`,
		},
		body: JSON.stringify({
			//operationName: null,
			query,
			variables,
		}),
	});

	const responseStr = await response.text();
	let responseJSON: {data: any, errors?: any[]};
	try {
		responseJSON = JSON.parse(responseStr);
	} catch (err) {
		throw new Error(`Failed to parse GraphQL response. @error:${ErrToStr(err)} @response:${responseStr}`);
	}

	if (responseJSON.errors) {
		throw new Error(`GraphQL Error: ${JSON.stringify(responseJSON.errors)}`);
	}

	return responseJSON.data;
}

export function RenderTargetToBase64(renderer: THREE.WebGLRenderer, renderTarget: THREE.WebGLRenderTarget, mimeType = "image/webp", quality = 0.9) {
	const width = renderTarget.width;
	const height = renderTarget.height;
	const pixelBuffer = new Float32Array(width * height * 4);
	renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixelBuffer);

	/*const totalValueInPixelBuffer = pixelBuffer.reduce((sum, value)=>sum + value, 0);
	console.log(`Total value in pixel buffer (${width}x${height}):`, totalValueInPixelBuffer);*/
	//console.log("First few float pixels:", pixelBuffer.slice(0, 16));
	//console.log("Min/Max values:", Math.min(...pixelBuffer.filter((a, i)=>i % 4 != 3)), Math.max(...pixelBuffer.filter((a, i)=>i % 4 != 3))); // exclude alpha channel for min/max // commented; causes crash for large images, fsr

	// Convert float values to byte values (0-255)
	// For lightmaps, values might be > 1.0, so you may need tone mapping
	const byteBuffer = new Uint8Array(width * height * 4);
	for (let i = 0; i < pixelBuffer.length; i++) {
		// Simple clamp to 0-1 range, then convert to 0-255
		// You might want to adjust this based on your lightmap values
		const clampedValue = Math.max(0, Math.min(1, pixelBuffer[i]));
		byteBuffer[i] = Math.round(clampedValue * 255);
	}

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;

	const ctx = canvas.getContext("2d")!;
	const imageData = ctx.createImageData(width, height);

	// Flip Y and copy data
	/*for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const srcIndex = ((height - y - 1) * width + x) * 4;
			const dstIndex = (y * width + x) * 4;

			imageData.data[dstIndex] = byteBuffer[srcIndex];
			imageData.data[dstIndex + 1] = byteBuffer[srcIndex + 1];
			imageData.data[dstIndex + 2] = byteBuffer[srcIndex + 2];
			imageData.data[dstIndex + 3] = byteBuffer[srcIndex + 3];
		}
	}*/
	// Copy data directly without flipping Y (we just use the asset-uploading for "dumb storage", ie. the same flipped-ness will be applied when the data gets loaded back)
	for (let i = 0; i < byteBuffer.length; i++) {
		imageData.data[i] = byteBuffer[i];
	}
	const totalValueInImageData = imageData.data.reduce((sum, value)=>sum + value, 0);
	console.log(`Total value in image-data (${imageData.width}x${imageData.height}):`, totalValueInImageData);

	ctx.putImageData(imageData, 0, 0);

	return new Promise<string>((resolve, reject)=>{
		// Use toBlob for better performance with large images
		canvas.toBlob(blob=>{
			if (!blob) {
				reject(new Error("Failed to convert canvas to Blob"));
				return;
			}
			const reader = new FileReader();
			reader.onload = ()=>resolve(reader.result as string);
			reader.readAsDataURL(blob);
		}, mimeType, quality);
	});
}
export function BlobOrBase64ToFloatBasedDataTexture(base64StringOrBlob: string | Blob) {
	return new Promise<THREE.DataTexture>(resolve=>{
		const img = new Image();
		img.onload = ()=>{
			// Create canvas and get pixel data
			const canvas = document.createElement("canvas");
			canvas.width = img.width;
			canvas.height = img.height;

			const ctx = canvas.getContext("2d")!;
			ctx.drawImage(img, 0, 0);

			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const pixels = imageData.data;

			// Convert back to float values
			const floatPixels = new Float32Array(pixels.length);
			for (let i = 0; i < pixels.length; i++) {
				// Convert 0-255 back to 0-1 float range
				// Apply inverse of whatever tone mapping you used
				floatPixels[i] = pixels[i] / 255.0;
			}

			// Create a DataTexture with float data
			const texture = new THREE.DataTexture(
				floatPixels,
				canvas.width,
				canvas.height,
				THREE.RGBAFormat,
				THREE.FloatType
			);
			texture.needsUpdate = true;
			resolve(texture);
		};

		img.src = base64StringOrBlob instanceof Blob ? URL.createObjectURL(base64StringOrBlob) : base64StringOrBlob; // handle both string and Blob input
	});
}
export function DrawTextureToRenderTarget(renderer: THREE.WebGLRenderer, renderTarget: THREE.WebGLRenderTarget, texture: THREE.Texture) {
	// Render to target
	const material = new THREE.MeshBasicMaterial({map: texture});
	const geometry = new THREE.PlaneGeometry(2, 2);
	const mesh = new THREE.Mesh(geometry, material);

	const scene = new THREE.Scene();
	scene.add(mesh);

	const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
	camera.position.z = 1;

	renderer.setRenderTarget(renderTarget);
	renderer.render(scene, camera);
	renderer.setRenderTarget(null);

	// Cleanup
	geometry.dispose();
	material.dispose();
	texture.dispose();
}